/* eslint-disable camelcase */
import Cookies from 'js-cookie'
import { UserManager } from 'oidc-client-ts'
import axios from 'axios'
import {
  ACCESS_TOKEN_COOKIE, ACCESS_TOKEN_TYPE, COOKIE_EXPIRY, REFRESH_TOKEN_COOKIE, RENEW_TOKEN_URL
} from './config'
import logger from '../../utilities/logger'

let accessToken = null
let refreshToken = null
/**
 * Global Auth Plugin for Vue.js using oidc-client-ts
 * Provides methods for authentication, user management, and token handling.
 * @module GlobalAuthPlugin
 * @requires oidc-client-ts
 * @requires js-cookie
 * @requires ../../utilities/logger
 * @example
 *  - Vue.use(GlobalAuthPlugin, { userManagerSettings: { <oidc-client-ts settings> } })
 * @property {UserManager} userManager - Instance of UserManager from oidc-client-ts
 * @property {function} login - Initiates the login process
 * @property {function} logout - Logs the user out and clears session
 * @property {function} getAccessToken - Gets the access token from cookie or user manager
 * @property {function} getRefreshToken - Gets the refresh token from cookie or user manager
 * @property {function} getTokenType - Gets the token type from configuration
 * @property {function} renewToken - Renews the access token if expired
 * @property {function} handleCallback - TBC: Handles the authentication callback after login
 *
 * @returns {Object} Auth methods and user information
 */
const GlobalAuthPlugin = {
  install(Vue, options) {
    if (!options || !options.userManagerSettings) {
      logger.error('OIDC Client requires instantiation settings')
      return
    }

    const userManager = new UserManager(options.userManagerSettings)
    /**
     * Updates authentication cookies with new access and refresh tokens.
     * If a token is null, it removes the corresponding cookie.
     * @param {Object} values - Object containing accessToken and refreshToken
     * @param {string|null} values.accessToken - New access token to set, or null to remove
     * @param {string|null} values.refreshToken - New refresh token to set, or null to remove
     * @returns {Promise<void>} Resolves when cookies are updated
     */
    async function updateAuthCookies(values = { accessToken: null, refreshToken: null }) {
      const { accessToken: newAccessToken, refreshToken: newRefreshToken } = values
      const cookieOptions = {
        expires: COOKIE_EXPIRY, // Set cookie to expire in 180 days
        secure: true,
        sameSite: '',
        httpOnly: ''
      }

      if (newAccessToken) {
        Cookies.set(ACCESS_TOKEN_COOKIE, newAccessToken, cookieOptions) // Set cookie to expire in 180 day
        accessToken = newAccessToken
      } else {
        Cookies.remove(ACCESS_TOKEN_COOKIE)
        accessToken = null
      }
      if (newRefreshToken) {
        Cookies.set(REFRESH_TOKEN_COOKIE, newRefreshToken, cookieOptions) // Set cookie to expire in 180 day
        refreshToken = newRefreshToken
      } else {
        Cookies.remove(REFRESH_TOKEN_COOKIE)
        refreshToken = null
      }
    }

    // Initialize access and refresh tokens from cookies
    accessToken = Cookies.get(ACCESS_TOKEN_COOKIE) || null
    refreshToken = Cookies.get(REFRESH_TOKEN_COOKIE) || null

    Vue.prototype.$auth = {
      userManager,

      /**
       * Initiates the login process by redirecting the user to the identity provider.
       * @param {Object} [config.returnUrl] - Optional argument providing return path after login, defaults to current path
       * @returns {Promise<void>} Resolves when the login process is initiated
       */
      async login(config = {}) {
        const returnUrl = config.returnUrl || window.location.href
        const redirect_uri = `${options.userManagerSettings.redirect_uri}?returnUrl=${encodeURIComponent(returnUrl)}`
        try {
          await this.userManager.signinRedirect({ redirect_uri })
        } catch (error) {
          logger.error(`Authentication login error: ${error}`, { config })
          throw error
        }
      },

      /**
       * TBC: Not in use throughout the app
       * Logs the user out by clearing cookies and session storage,
       * then redirects to the identity provider's logout endpoint.
       * @param {Object} [config.returnUrl] - Optional argument providing return path after login, defaults to current path
       * @returns {Promise<void>} Resolves when the logout process is initiated
       */
      async logout(config = {}) {
        const returnUrl = config.returnUrl || window.location.href
        const post_logout_redirect_uri = `${options.userManagerSettings.post_logout_redirect_uri}?returnUrl=${encodeURIComponent(returnUrl)}`
        try {
          // Remove cookies
          updateAuthCookies({ accessToken: null, refreshToken: null })

          const loggedout = await this.userManager.signoutRedirect({ post_logout_redirect_uri })
          return loggedout
        } catch (error) {
          logger.error('Authentication logout error:', error)
          throw error
        }
      },

      /**
       * Gets the Access token for the current user.
       * @returns {Promise<string|null>} Resolves to the access token if available, null otherwise
       */
      async getAccessToken() {
        const storedAccessToken = accessToken || Cookies.get(ACCESS_TOKEN_COOKIE)
        if (storedAccessToken) {
          return storedAccessToken
        } else if (this.userManager && this.userManager.getUser) {
          try {
            const user = await this.userManager.getUser()
            if (user?.access_token) {
              accessToken = user.access_token
              refreshToken = user.refresh_token || null

              // Store the access token in a cookie
              updateAuthCookies({ accessToken, refreshToken })

              return accessToken
            }
          } catch (error) {
            logger.error('Error getting user in getAccessToken:', error)
          }
        }
        return null
      },

      /**
       * Gets the Refresh token for the current user.
       * @returns {Promise<string|null>} Resolves to the refresh token if available, null otherwise
       */
      async getRefreshToken() {
        const storedRefreshToken = refreshToken || Cookies.get(REFRESH_TOKEN_COOKIE)
        if (storedRefreshToken) {
          return storedRefreshToken
        } else if (this.userManager && this.userManager.getUser) {
          try {
            const user = await this.userManager.getUser()
            if (user?.refresh_token) {
              // Store the refresh token in a cookie
              accessToken = user.access_token || null
              refreshToken = user.refresh_token
              updateAuthCookies({ accessToken, refreshToken })

              return refreshToken
            }
          } catch (error) {
            // Remove cookies and set token values to null on error
            updateAuthCookies({ accessToken: null, refreshToken: null })
            logger.error('Error getting user in getRefreshToken:', error)
          }
        }
        return null
      },

      /**
       * Gets the token type for the current user.
       * @returns {Promise<string|null>} Resolves to the token type if available, null otherwise
       */
      async getTokenType() {
        return ACCESS_TOKEN_TYPE
      },

      /**
       * Renews the access token if it is expired or not present.
       * Uses the refresh token to obtain a new access token.
       * @returns {Promise<string|null>} Resolves to the new access token if successful, null otherwise
       */
      async renewToken() {
        accessToken = await this.getAccessToken()
        refreshToken = await this.getRefreshToken()


        // Check if token is expired or not present
        if (accessToken && refreshToken) {
          const refresh_uri = RENEW_TOKEN_URL
          const { authority, client_id } = options.userManagerSettings
          const payload = {
            client_id,
            grant_type: 'refresh_token',
            refresh_token: refreshToken
          }

          return axios.post(`${authority}${refresh_uri}`, payload, {
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded'
            }
          }).then(response => {
            const { access_token, refresh_token } = response.data

            // Store and update cookies with new tokens
            accessToken = access_token || null
            refreshToken = refresh_token || null
            updateAuthCookies({ accessToken, refreshToken })

            return access_token
          }).catch(async error => {
            logger.error('Token renewal failed:', error)
            // Remove cookies and set token values to null on error
            updateAuthCookies({ accessToken: null, refreshToken: null })

            // If renew fails, force logout
            await this.logout()
            return null
          })
        }
        return null
      },

      /**
       * Handles Auth call back, providing args and user
       * @returns {Object} user
       */
      async handleCallback() {
        try {
          const user = await this.userManager.signinCallback()
          // Store the access token in a cookie
          accessToken = user.access_token || null
          refreshToken = user.refresh_token || null
          if (!accessToken || !refreshToken) {
            logger.warn('No access or refresh token found in user object after signinCallback')
          } else {
            updateAuthCookies({ accessToken, refreshToken })
            window.localStorage.removeItem('notification') // Clear local storage account creation notification
          }
          return user
        } catch (error) {
          logger.error('Signin Callback error:', error)
          return null
        }
      }
    }
  }
}

export default GlobalAuthPlugin
